import { Box, Grid, IconButton, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import DatePickerField from '../../../common/DatePickerField';
import EditIcon from '../../../common/icons/EditIcon';
import SelectField from '../../../common/SelectField';
import { PERMISSION_IDS } from '../../../../constants/permissions';
import { usePermissions } from '../../../../context/PermissionContext';
import type { DropdownOption, UserProfileData } from '../../../../services/types/userProfile.types';
import { formatDate } from '../../../../utils/date';

export interface TeamInfoCardProps {
  data: UserProfileData;
  isEditMode: boolean;
  setIsEditMode?: (isEditMode: boolean) => void;
  onChange: (patch: Partial<UserProfileData>) => void;
  functionalTeams: DropdownOption[];
}

const TeamInfoCard: React.FC<TeamInfoCardProps> = ({
  data,
  isEditMode,
  setIsEditMode,
  onChange,
  functionalTeams,
}) => {
  const [isEdit, setIsEdit] = useState(false);
  const { hasPermission } = usePermissions();

  useEffect(() => {
    if (isEditMode === false) {
      setIsEdit(false);
    }
  }, [isEditMode]);

  const teamOptions = useMemo(
    () => functionalTeams.map((d) => ({ value: d.id, label: d.name })),
    [functionalTeams],
  );

  const primaryLabel =
    data.functionalTeamId == null
      ? 'N/A'
      : functionalTeams.find((d) => d.id === data.functionalTeamId)?.name ?? data.functionalTeamName ?? 'N/A';
  const secondaryLabel =
    data.functionalTeamIdSecondary == null
      ? 'N/A'
      : functionalTeams.find((d) => d.id === data.functionalTeamIdSecondary)?.name ??
        data.secondaryTeamName ??
        'N/A';

  return (
    <Box className="view-section" sx={{ bgcolor: '#F1F3FF', borderRadius: '8px', p: '20px', mb: 2.5 }}>
      <Box sx={{ position: 'relative' }}>
        <Box sx={{ position: 'absolute', top: '-12px', right: '-12px' }}>
          {!isEdit && hasPermission(PERMISSION_IDS.USER_PROFILE_EDIT) && (
            <IconButton
              className="view-edit-icon"
              aria-label="Edit functional teams"
              onClick={() => {
                setIsEdit(true);
                setIsEditMode?.(true);
              }}
            >
              <EditIcon />
            </IconButton>
          )}
        </Box>
        <Typography sx={{ fontSize: '16px', fontWeight: 600, mb: 2, color: '#101828' }}>Functional teams</Typography>
      {!isEdit ? (
        <>
          <Box className="view-row-box">
            <Box className="view-column-box">
              <div className="view-label-title">Functional team</div>
              <div className="view-value-title">{primaryLabel}</div>
            </Box>
            <Box className="view-column-box">
              <div className="view-label-title">Team joined date</div>
              <div className="view-value-title">{data.teamJoinedDate ? formatDate(data.teamJoinedDate) : 'N/A'}</div>
            </Box>
          </Box>
          <Box className="view-row-box" sx={{ mt: 2.5 }}>
            <Box className="view-column-box">
              <div className="view-label-title">Secondary functional team</div>
              <div className="view-value-title">{secondaryLabel}</div>
            </Box>
            <Box className="view-column-box">
              <div className="view-label-title">Secondary team joined date</div>
              <div className="view-value-title">
                {data.secondaryTeamJoinedDate ? formatDate(data.secondaryTeamJoinedDate) : 'N/A'}
              </div>
            </Box>
          </Box>
        </>
      ) : (
        <Grid container spacing={2.5}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <SelectField
              label="Functional team"
              value={data.functionalTeamId ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                const id = v === '' ? null : Number(v);
                const name = id == null ? null : functionalTeams.find((t) => t.id === id)?.name ?? null;
                onChange({ functionalTeamId: id, functionalTeamName: name });
              }}
              options={teamOptions}
              placeholder="None"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <DatePickerField
              label="Team joined date"
              value={data.teamJoinedDate ? dayjs(data.teamJoinedDate) : null}
              onChange={(date) => onChange({ teamJoinedDate: date })}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <SelectField
              label="Secondary functional team"
              value={data.functionalTeamIdSecondary ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                const id = v === '' ? null : Number(v);
                const name = id == null ? null : functionalTeams.find((t) => t.id === id)?.name ?? null;
                onChange({ functionalTeamIdSecondary: id, secondaryTeamName: name });
              }}
              options={teamOptions}
              placeholder="None"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <DatePickerField
              label="Secondary team joined date"
              value={data.secondaryTeamJoinedDate ? dayjs(data.secondaryTeamJoinedDate) : null}
              onChange={(date) => onChange({ secondaryTeamJoinedDate: date })}
            />
          </Grid>
        </Grid>
      )}
      </Box>
    </Box>
  );
};

export default TeamInfoCard;
